import type { ServerErrorContext, ServerErrorInput } from "@voltie/localization-web";

import { HttpError, NetworkError } from "@/lib/http";
import { serverErrors, type Translate } from "./localization";

// Display of failed requests (LOC-707). The text is always a catalog message of
// the shared resolver: a registered family code, a server `l10n` id, a status
// generic or the screen's own fallback. Server, transport and parser text is
// never shown, and nothing here decides a branch; callers keep reading status
// and legacy codes (`errorCode`) for those.

export type ErrorContext = ServerErrorContext;

/** What the resolver may read from a failure; anything that is not a request failure gives only the screen's fallback. */
export function serverErrorInput(error: unknown): ServerErrorInput {
  if (error instanceof HttpError) return { status: error.status, body: error.body };
  if (error instanceof NetworkError) return { network: true };
  return {};
}

/** The catalog message for a failed request, in the language active when it is called (call it at render or toast time). */
export function errorMessage(t: Translate, error: unknown, context: ErrorContext): string {
  return serverErrors.message(t, serverErrorInput(error), context);
}

/** The legacy machine code a failed request carries in `body[field]`, for branches only. */
export function errorCode(error: unknown, field: "error" | "code" | "error_code" | "reason" = "error"): string | undefined {
  if (!(error instanceof HttpError)) return undefined;
  const body = error.body;
  if (typeof body !== "object" || body === null || Array.isArray(body) || !Object.hasOwn(body, field)) return undefined;
  const code = (body as Record<string, unknown>)[field];
  return typeof code === "string" ? code : undefined;
}
